import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { BadgeCounts, NotificationDto, PaginationMeta } from '@biyolink/shared';
import { apiClient, postData } from './client';
import { useAuthStore } from '@/stores/auth-store';

/** Zil ve sidebar rozetleri için yoklama aralığı — mesaj dizisinden seyrek. */
const BADGE_POLL_INTERVAL_MS = 30_000;

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (unread: boolean, page: number) => ['notifications', 'list', unread, page] as const,
  badges: ['notifications', 'badges'] as const,
};

export function useNotifications(options: { unread?: boolean; page?: number } = {}) {
  const unread = options.unread ?? false;
  const page = options.page ?? 1;

  return useQuery({
    queryKey: notificationKeys.list(unread, page),
    queryFn: async (): Promise<{ items: NotificationDto[]; meta: PaginationMeta }> => {
      const response = await apiClient.get<{ data: NotificationDto[]; meta: PaginationMeta }>(
        '/notifications',
        { params: { page, ...(unread ? { unread: 1 } : {}) } },
      );
      return { items: response.data.data, meta: response.data.meta };
    },
    placeholderData: (previous) => previous,
  });
}

/** Okunmamış bildirim/mesaj ve "sıra sende" teklif sayaçları; oturum yoksa istek atılmaz. */
export function useBadgeCounts() {
  const isAuthenticated = useAuthStore((state) => state.user !== null);

  return useQuery({
    queryKey: notificationKeys.badges,
    queryFn: async (): Promise<BadgeCounts> => {
      const response = await apiClient.get<{ data: BadgeCounts }>('/notifications/badges');
      return response.data.data;
    },
    enabled: isAuthenticated,
    refetchInterval: BADGE_POLL_INTERVAL_MS,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => postData<NotificationDto>(`/notifications/${id}/read`),
    onSuccess: () => invalidateNotifications(queryClient),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => postData<{ updated: number }>('/notifications/read-all'),
    onSuccess: () => invalidateNotifications(queryClient),
  });
}

async function invalidateNotifications(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
}
